import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericCommentObjectType, GenericComment } from '../../Types/genericComment';
import {
	useGetGenericComments,
	useAddGenericComment,
	useUpdateGenericComment,
	useDeleteGenericComment,
} from '../../Hooks/useGenericComment';
import { BizButton } from '../Mini/BizButton';
import BizTextArea from '../Mini/BizTextArea';
import BizIconButton from '../Mini/BizIconButton';
import './GenericComments.scss';
import { formatToTodayString } from '../../Utils/dateUtils';
import { useGlobalState } from '../../Contexts/globalStateContext';

type GenericCommentsProps = {
	readOnly?: boolean;
	objectType: GenericCommentObjectType;
	objectId: number;
	isAdmin?: boolean;
};

const GenericComments = ({ readOnly = false, objectType, objectId, isAdmin = false }: GenericCommentsProps) => {
	const { t } = useTranslation();
	const { data: comments, statusState } = useGetGenericComments(objectType, objectId);
	const { addComment, statusState: addCommentStatus } = useAddGenericComment();
	const { updateComment, statusState: updateCommentStatus } = useUpdateGenericComment();
	const { deleteComment } = useDeleteGenericComment();
	const {
		state: { user },
	} = useGlobalState();

	const [newComment, setNewComment] = useState('');
	const [replyingTo, setReplyingTo] = useState<number | null>(null);
	const [replyText, setReplyText] = useState('');
	const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
	const [editText, setEditText] = useState('');

	const handleSubmitComment = () => {
		if (!newComment.trim()) return;

		addComment({
			Comment: newComment,
			ObjectType: objectType,
			ObjectId: objectId,
		});

		setNewComment('');
	};

	const handleSubmitReply = (parentId: number) => {
		if (!replyText.trim()) return;

		addComment({
			Comment: replyText,
			ObjectType: objectType,
			ObjectId: objectId,
			ParentId: parentId,
		});

		setReplyText('');
		setReplyingTo(null);
	};

	const handleStartReply = (commentId: number) => {
		setReplyingTo(commentId);
		setReplyText('');
	};

	const handleCancelReply = () => {
		setReplyingTo(null);
		setReplyText('');
	};

	const handleStartEdit = (comment: GenericComment) => {
		setEditingCommentId(comment.Id);
		setEditText(comment.Comment);
	};

	const handleCancelEdit = () => {
		setEditingCommentId(null);
		setEditText('');
	};

	const handleSaveEdit = (commentId: number) => {
		if (!editText.trim()) return;

		updateComment({
			Id: commentId,
			Comment: editText,
			ObjectType: objectType,
			ObjectId: objectId,
		});

		setEditingCommentId(null);
		setEditText('');
	};

	const handleDeleteComment = (commentId: number) => {
		if (window.confirm(t('GENERIC_COMMENTS.CONFIRM_DELETE'))) {
			deleteComment(commentId, objectType, objectId);
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return formatToTodayString(date);
	};

	const canEditOrDeleteComment = (comment: GenericComment) => {
		if (!user) return false;

		// Admin can always edit/delete
		if (isAdmin) return true;

		// User can edit/delete own comments only
		if (comment.UserId !== user.Id) return false;

		// Check if comment has replies (users can't edit/delete if there are replies)
		const hasReplies = replies.some((reply) => reply.ParentId === comment.Id);
		return !hasReplies;
	};

	const renderComment = (comment: GenericComment, isReply = false) => (
		<div key={comment.Id} className={`biz-comment ${isReply ? 'biz-comment-reply' : ''}`}>
			<div className='biz-comment-header'>
				<span className='biz-comment-author'>{comment.AuthorName || t('GENERIC_COMMENTS.ANONYMOUS')}</span>
				<span className='biz-comment-date'>{formatDate(comment.Date)}</span>
				{(!readOnly || isAdmin) && canEditOrDeleteComment(comment) && (
					<div className='biz-comment-actions'>
						<BizIconButton icon='EDIT' onClick={() => handleStartEdit(comment)} title={t('GENERIC_COMMENTS.EDIT_COMMENT')} noBackground />
						<BizIconButton
							icon='TRASH'
							onClick={() => handleDeleteComment(comment.Id)}
							title={t('GENERIC_COMMENTS.DELETE_COMMENT')}
							noBackground
						/>
					</div>
				)}
			</div>

			{editingCommentId === comment.Id ? (
				<div className='biz-comment-reply-form'>
					<BizTextArea
						value={editText}
						onChange={(e) => setEditText(e.target.value)}
						placeholder={t('GENERIC_COMMENTS.EDIT_PLACEHOLDER')}
						rows={3}
						className='form-control'
					/>
					<div className='biz-comment-reply-actions'>
						<BizButton
							text={t('GENERIC_COMMENTS.SAVE')}
							onClick={() => handleSaveEdit(comment.Id)}
							isLead={true}
							state={updateCommentStatus}
							disabled={!editText.trim()}
						/>
						<BizButton text={t('GENERIC_COMMENTS.CANCEL')} onClick={handleCancelEdit} />
					</div>
				</div>
			) : (
				<div className='biz-comment-content'>{comment.Comment}</div>
			)}

			{!readOnly && !isReply && editingCommentId !== comment.Id && replyingTo !== comment.Id && (
				<div className='biz-comment-actions'>
					<BizButton
						text={t('GENERIC_COMMENTS.REPLY')}
						onClick={() => handleStartReply(comment.Id)}
						className='biz-comment-reply-btn'
					/>
				</div>
			)}
			{replyingTo === comment.Id && (
				<div className='biz-comment-reply-form'>
					<BizTextArea
						value={replyText}
						onChange={(e) => setReplyText(e.target.value)}
						placeholder={t('GENERIC_COMMENTS.REPLY_PLACEHOLDER')}
						rows={3}
						className='form-control'
					/>
					<div className='biz-comment-reply-actions'>
						<BizButton
							text={t('GENERIC_COMMENTS.REPLY')}
							onClick={() => handleSubmitReply(comment.Id)}
							isLead={true}
							state={addCommentStatus}
						/>
						<BizButton text={t('GENERIC_COMMENTS.CANCEL')} onClick={handleCancelReply} />
					</div>
				</div>
			)}
		</div>
	);

	const mainComments = comments.filter((comment) => comment.ParentId === -1);
	const replies = comments.filter((comment) => comment.ParentId && comment.ParentId > 0);

	if (statusState === 'LOADING') {
		return <div className='biz-generic-comments loading'>{t('GENERIC_COMMENTS.LOADING')}</div>;
	}

	return (
		<div className='biz-generic-comments'>
			<div className='biz-comments-header'>
				<h3>{t('GENERIC_COMMENTS.HEADER', { count: comments.length })}</h3>
			</div>

			{!readOnly && (
				<div className='biz-comment-form'>
					<BizTextArea
						value={newComment}
						onChange={(e) => setNewComment(e.target.value)}
						placeholder={t('GENERIC_COMMENTS.WRITE_COMMENT_PLACEHOLDER')}
						rows={4}
						className='form-control'
					/>
					<div className='biz-comment-form-actions'>
						<BizButton
							text={t('GENERIC_COMMENTS.ADD_COMMENT')}
							onClick={handleSubmitComment}
							isLead={true}
							state={addCommentStatus}
							disabled={!newComment.trim()}
						/>
					</div>
				</div>
			)}

			<div className='biz-comments-list'>
				{mainComments.map((comment) => (
					<div key={comment.Id} className='biz-comment-thread'>
						{renderComment(comment)}
						<div className='biz-comment-replies'>
							{replies.filter((reply) => reply.ParentId === comment.Id).map((reply) => renderComment(reply, true))}
						</div>
					</div>
				))}
			</div>

			{comments.length === 0 && (
				<div className='biz-no-comments'>
					{t('GENERIC_COMMENTS.NO_COMMENTS_YET')} {!readOnly && t('GENERIC_COMMENTS.BE_FIRST_TO_COMMENT')}
				</div>
			)}
		</div>
	);
};

export default GenericComments;
